import React, { useState, useEffect, useRef, useMemo } from 'react'
import Alert from '@mui/material/Alert'
import { Flex, Spin } from 'antd'
import { selectCurrentZone } from '../../store/global'
import BusinessesSummary from './BusinessSummary/BusinessSummary'
import { CurrentZone } from 'utils/types'
import { FatcaEntityForTable } from './types'
import { useVerticalEntitiesQuery } from 'hooks/useVerticalEntitiesQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import cx from 'classnames'
import messages from './messages'
import { IFatcaOverviewData } from './BusinessSummary/utils'
import { useFatcaOverviewQuery } from 'hooks/useFatcaOverviewQuery'
import { Filter, FilterAdditionalData } from 'components/FilterDrawer/FilterDrawer.config'
import { filtersInitialState } from './FatcaTablePage.config'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import { useEmptyColumnsQuery } from 'hooks/useEmptyColumnsQuery'
import VerticalTable from 'UI/VerticalTable/VerticalTable'
import ExportCSVTableVertical from 'components/ExportCSVTableVertical/ExportCSVTableVertical'
import VerticalTableFilters from 'components/VerticalTableFilters/VerticalTableFilters'
import Search from 'UI/Search/Search'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { fatcaColumns, keysForEmptyColumnsRequest } from './utils'
import { PAGE_SIZE } from 'components/verticals/constants'
import styles from 'styles/TablePageCommon.scss'
import NoDataContainer from 'components/NoDataContainer'

const FatcaTablePage = (): JSX.Element => {
  const currentZone: CurrentZone = useSelector(selectCurrentZone)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const statisticsActivityRef = useRef<HTMLDivElement | null>(null)
  const navigationRef = useRef<HTMLDivElement | null>(null)

  const [activeFilters, setActiveFilters] = useState<Filter[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const [filterAdditionalData, setFilterAdditionalData] = useState<FilterAdditionalData[]>([])

  const { searchValue, searchValueDebounced, sorting, setSearchValue, setSorting } = useVerticalSearchAndSort()

  const { data: emptyColumnsData, isFetched: emptyColumnsIsFetched } = useEmptyColumnsQuery({
    verticalIndices: currentZone?.fatcaIndices,
    currentQueryKey: 'fatca-entities-key',
    keys: keysForEmptyColumnsRequest,
  })

  const { data: overviewData } = useFatcaOverviewQuery<IFatcaOverviewData>({
    verticalIndices: currentZone?.fatcaIndices,
    currentQueryKey: 'fatca-overview-key',
    searchValue: searchValueDebounced,
    filters: activeFilters,
  })

  const { verticalEntities, fetchNextPage, isFetching, isLoading, isError } =
    useVerticalEntitiesQuery<FatcaEntityForTable>({
      verticalIndices: currentZone?.fatcaIndices,
      currentQueryKey: 'fatca-entities-key',
      searchValue: searchValueDebounced,
      sorting,
      filters: activeFilters,
      pageSize: PAGE_SIZE,
    })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: currentZone?.fatcaIndices,
    currentQueryKey: 'fatca-filter-options-key',
    filtersInitialState,
  })

  useEffect(() => {
    if (overviewData && filterAdditionalData.length === 0) {
      setFilterAdditionalData([
        {
          name: 'totals.unreportedRevenue',
          minLimit: overviewData.totalUnreportedEarningRange.min,
          maxLimit: overviewData.totalUnreportedEarningRange.max,
        },
      ])
    }
  }, [overviewData, filterAdditionalData])

  const hasNoData = useMemo(() => {
    return !isLoading && !isError && (!verticalEntities || verticalEntities.length === 0) && !searchValueDebounced && activeFilters.length === 0
  }, [isLoading, isError, verticalEntities, searchValueDebounced, activeFilters])

  return (
    <div ref={containerRef} className={styles.container}>
      <div ref={navigationRef}>
        <header className='page-header withNavigationHeader'>
          <h1 className={cx('page-title', 'h1')}>
            <FormattedMessage {...messages.header} />
          </h1>
        </header>
      </div>
      {hasNoData ? (
        <NoDataContainer verticalName="FATCA" zoneName={currentZone?.name} />
      ) : (
        <>
          <section ref={statisticsActivityRef} className={styles.summary} id={'businessSummary'}>
            <BusinessesSummary overviewData={overviewData} />
          </section>
          {isLoading ? (
            <Flex align='center' gap='middle' justify='center' style={{ padding: '45px 14px' }}>
              <Spin size='large' />
            </Flex>
          ) : isError ? (
            <Alert severity='error' sx={{ fontFamily: 'Source Sans Pro', fontSize: '1rem', alignItems: 'center' }}>
              Something went wrong
            </Alert>
          ) : (
            <>
              {emptyColumnsIsFetched && (
                <VerticalTable
                  pageSize={PAGE_SIZE}
                  enableColumnPicker
                  tableId="fatca"
                  dataSource={verticalEntities as any[]}
                  columns={fatcaColumns}
                  containerRef={containerRef}
                  additionalRefs={[statisticsActivityRef, navigationRef]}
                  fetchNextPage={fetchNextPage}
                  total={overviewData?.totalEntities || 0}
                  loading={isFetching}
                  disabledColumns={emptyColumnsData}
                  onSelectionChange={setSelectedIds}
                  removeFromPickerColumns={[COLUMNS_IDS.profileNames]}
                  rowClassName={styles.tableRow}
                  onRow={() => ({
                    style: { cursor: 'default' },
                  })}
                  onChange={(pagination, filters, sorter) => {
                    if (!Array.isArray(sorter)) {
                      if (sorter.order) {
                        setSorting([{ id: sorter.columnKey as string, desc: sorter.order === 'descend' }])
                      } else {
                        setSorting([])
                      }
                    }
                  }}
                >
                  <div className={styles.counter}>
                    {`Fatca: `}
                    <span>{selectedIds.length > 0 ? `${selectedIds.length} selected` : overviewData?.totalEntities}</span>
                  </div>
                  <VerticalTableFilters
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                    filtersInitialState={filtersInitialState}
                    filterOptions={filterOptions}
                    filterAdditionalData={filterAdditionalData}
                  />
                  <Search
                    placeholder='input search text'
                    enterButton
                    allowClear
                    style={{ width: 264 }}
                    loading={isFetching}
                    onSearch={newSearchValue => setSearchValue(newSearchValue)}
                  />
                  <ExportCSVTableVertical
                    zoneIndex={currentZone?.fatcaIndices}
                    csvFileName='FATCA'
                    searchValue={searchValue}
                    selectedIds={selectedIds}
                    sorting={sorting}
                    total={overviewData?.totalEntities}
                  />
                </VerticalTable>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

export default FatcaTablePage
